import { useEffect, useState } from "react";
import { useOrderParticipantListTemplate } from "./useOrderParticipantListTemplate";
import { useParticipantListEditor } from "./useParticipantListEditor";
import { useParticipantLists } from "./useParticipantLists";

export interface UseParticipantListsPageStateOptions {
  readonly signedLinkToken?: string;
}

export const useParticipantListsPageState = (orderId: string, options: UseParticipantListsPageStateOptions = {}) => {
  const signedLinkToken = options.signedLinkToken;

  const { template, isLoading: isTemplateLoading } = useOrderParticipantListTemplate(orderId, { signedLinkToken });
  const {
    participantLists,
    isLoading: areParticipantsLoading,
    addParticipantList,
    uploadSpreadsheet,
    updateStatus,
    sendToProviders,
  } = useParticipantLists(orderId, { signedLinkToken });

  const [editMode, setEditMode] = useState(false);
  const [selectedVersionIdx, setSelectedVersionIdx] = useState<number | undefined>(undefined);

  useEffect(() => {
    setEditMode(false);
    setSelectedVersionIdx(undefined);
  }, [orderId]);

  const lastSavedList = participantLists.at(-1);
  const editor = useParticipantListEditor({
    template,
    previousParticipantList: lastSavedList,
    onAdd: async (request) => {
      await addParticipantList(request);
    },
  });

  const isBootstrapping = isTemplateLoading || areParticipantsLoading;
  const noListsYet = participantLists.length === 0;
  const showEditor = noListsYet || editMode;
  const resolvedVersionIdx = selectedVersionIdx ?? participantLists.length - 1;
  const selectedList = participantLists[resolvedVersionIdx];
  const previousList = resolvedVersionIdx > 0 ? participantLists[resolvedVersionIdx - 1] : undefined;
  const noParticipantsRequired = template ? template.combinations.length === 0 : false;
  const exportTargets = template?.spreadsheetExportTargets ?? [];

  const handleEdit = () => setEditMode(true);

  const handleCancelEdit = () => {
    setEditMode(false);
    setSelectedVersionIdx(undefined);
  };

  const handleSave = async () => {
    await editor.handleSubmit();
    setEditMode(false);
  };

  const handleUploadSpreadsheet = async (file: File) => {
    await uploadSpreadsheet(file);
    setEditMode(false);
  };

  return {
    template,
    isBootstrapping,
    participantLists,
    updateStatus,
    sendToProviders,
    editMode,
    selectedVersionIdx,
    setSelectedVersionIdx,
    editor,
    lastSavedList,
    showEditor,
    resolvedVersionIdx,
    selectedList,
    previousList,
    noParticipantsRequired,
    exportTargets,
    handleEdit,
    handleCancelEdit,
    handleSave,
    handleUploadSpreadsheet,
  };
};
